import React, { useState, useEffect, useMemo } from 'react';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
    Chip
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MonoText from "components/MonoText";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import OrderAddDialog from 'layouts/Travel/orders/OrderAddDialog';
import {useNavigate} from "react-router-dom";

const TravelOrderPax = () => {
    const [pax, setPax] = useState([]);
    const [orderNumberById, setOrderNumberById] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isChooserOpen, setIsChooserOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const { canCreate } = usePermission('travel_order_pax');
    const navigate = useNavigate();

    useEffect(() => {
        fetchPax();
    }, []);

    const fetchPax = async () => {
        setIsLoading(true);
        try {
            const [paxResponse, ordersResponse] = await Promise.all([
                axiosInstance.get('/travel-order-pax/'),
                axiosInstance.get('/travel-orders/'),
            ]);
            setPax(paxResponse.data || []);

            const orderMap = {};
            (ordersResponse.data || []).forEach((order) => {
                orderMap[order.id] = order.order_id || order.name || `Order #${order.id}`;
            });
            setOrderNumberById(orderMap);
        } catch (error) {
            console.error('Error fetching travel order pax:', error);
            setSnackbar({ open: true, message: 'Error loading travel order passengers', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewPax = (order) => {
        navigate(`/travel-order-pax/${order.id}`);
    };

    const columns = useMemo(() => [
        {
            Header: "Passenger Name",
            accessor: "passenger_name",
            width: "15%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleViewPax(row.original)}
                >
                    <MonoText component="span" onClick={() => handleViewPax(row.original)} color="info" fontWeight="medium" sx={{ cursor: 'pointer' }}>
                        {value}
                    </MonoText>
                </MDTypography>
            ),
        },
        {
            Header: "Travel Order",
            accessor: "travel_order_id",
            width: "14%",
            Cell: ({ value }) => (
                <MonoText>
                    {orderNumberById[value] || value || '—'}
                </MonoText>
            )
        },
        {
            Header: "Email",
            accessor: "email",
            width: "18%"
        },
        {
            Header: "Mobile",
            accessor: "mobile",
            width: "12%"
        },
        {
            Header: "Gender",
            accessor: "gender",
            width: "8%"
        },
        {
            Header: "Primary",
            accessor: "is_primary",
            width: "8%",
            Cell: ({ value }) => value ? <Chip label="Yes" size="small" color="primary" /> : '-'
        },
        {
            Header: "Status",
            accessor: "status",
            width: "10%",
            Cell: ({ value }) => (
                <Chip
                    label={value || 'Active'}
                    size="small"
                    color={value === 'Active' ? 'success' : 'error'}
                />
            )
        },
        {
            Header: "Is Travelling",
            accessor: "is_travelling",
            width: "10%",
            Cell: ({ value }) => value ? 'Yes' : 'No'
        },
    ], [orderNumberById]);

    const tableData = useMemo(() => ({
        columns,
        rows: pax,
    }), [columns, pax]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Travel Order Passengers</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={() => setIsChooserOpen(true)}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Passenger
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : pax.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Travel Order Passenger" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            <OrderAddDialog
                open={isChooserOpen}
                onClose={() => setIsChooserOpen(false)}
                dialogTitle="Add Passenger"
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default TravelOrderPax;
